import { ChangeDetectionStrategy, Component, Inject, OnDestroy, Optional } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { getScopeGroupsAll } from '@modules/api-management/store/selectors/api-managment.selectors';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { ApiKey } from '@shared/models/api-management/api-key';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Scope } from '@shared/models/api-management/scope';
import { ScopeGroup } from '@shared/models/api-management/scope-group';
import {
  activateApiKey,
  createApiKey,
  revokeApiKey,
  updateApiKey,
} from '@modules/api-management/store/actions/api-managment.actions';
import { isModalConfirmed, ModalShow } from '@shared/modal';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { isTruthy } from '@shared';

export interface TreeDataObject {
  id: string;
  name: string;
  parentId: string;
}

interface ScopeNode {
  name: string;
  id: string;
}

interface ScopeGroupNode {
  name: string;
  id: string;
  children: ScopeNode[];
}

class ScopeGroupFlatNode {
  name: string;
  id: string;
  expandable: boolean;
  level: number;
}

export interface ApiKeyFormData {
  apiKey?: ApiKey;
}

@Component({
  selector: 'mojo-api-key-form',
  templateUrl: './api-key-form.component.html',
  styleUrls: ['./api-key-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiKeyFormComponent implements OnDestroy {
  scopeGroups$ = this.store.select(getScopeGroupsAll);
  treeControl: FlatTreeControl<ScopeGroupFlatNode>;
  nestedNodeMap = new Map<TreeDataObject, ScopeGroupFlatNode>();
  flatNodeMap = new Map<ScopeGroupFlatNode, TreeDataObject>();

  treeFlattener: MatTreeFlattener<ScopeGroupNode, ScopeGroupFlatNode>;

  dataSource: MatTreeFlatDataSource<ScopeGroupNode, ScopeGroupFlatNode>;
  confirmSubscription: Subscription;

  apiKey: ApiKey;
  title: string;
  scopesFormControl = this.fb.control([]);
  form = this.fb.group({
    name: [null, [Validators.required, Validators.maxLength(50)]],
    scopes: this.scopesFormControl,
  });
  isConfirmed$ = this.store.pipe(select(isModalConfirmed), filter(isTruthy));

  private subs = new Subscription();

  get selectedScopes(): Scope[] {
    return this.scopesFormControl.value || [];
  }

  set selectedScopes(scopes: Scope[]) {
    this.scopesFormControl.setValue(scopes);
  }

  constructor(
    private store: Store,

    @Optional() @Inject(MAT_DIALOG_DATA) private props: ApiKeyFormData,
    private fb: UntypedFormBuilder,
    @Optional() public dialogRef: MatDialogRef<ApiKeyFormComponent>,
  ) {
    this.apiKey = this.props?.apiKey;
    this.title = this.apiKey ? 'Edit API key' : 'Create new API key';
    if (this.apiKey) {
      this.form.patchValue(this.apiKey, { emitEvent: false });
    }
    this.subs = this.scopeGroups$.subscribe((groups) => {
      this.treeFlattener = new MatTreeFlattener(
        this.transformer,
        this.getLevel,
        this.isExpandable,
        this.getChildren,
      );
      this.treeControl = new FlatTreeControl<ScopeGroupFlatNode>(this.getLevel, this.isExpandable);
      this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
      this.dataSource.data = groups.map((group) => ({
        ...group,
        children: group.scopes,
      }));
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  transformer = (node: any, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode: ScopeGroupFlatNode =
      existingNode && existingNode.id === node.id ? existingNode : new ScopeGroupFlatNode();
    flatNode.id = node.id;
    flatNode.name = node.name;
    flatNode.level = level;
    flatNode.expandable = !!node.children?.length;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  getLevel = (node) => node.level;

  isExpandable = (node: ScopeGroupFlatNode) => node.expandable;

  getChildren = (node: ScopeGroupNode) => node.children;

  hasChild = (_: number, node: ScopeGroupFlatNode) => node.expandable;

  isNodeSelectedDos(e: { node: ScopeGroupFlatNode }): boolean {
    return this.isScopeSelected(e.node.id);
  }

  descendantsAllSelectedDos(e: { flatNode: ScopeGroupFlatNode }): boolean {
    const scopeGroup: ScopeGroup = this.flatNodeMap.get(e.flatNode);
    return !scopeGroup.scopes.some(
      (scope) => !this.selectedScopes.find((ss) => ss.id === scope.id),
    );
  }

  descendantsPartiallySelectedDos(e: { flatNode: ScopeGroupFlatNode }): boolean {
    const scopeGroup: ScopeGroup = this.flatNodeMap.get(e.flatNode);
    const allSelected = this.descendantsAllSelectedDos({ flatNode: e.flatNode });
    const someSelected = scopeGroup.scopes.some((scope) =>
      this.selectedScopes.find((ss) => ss.id === scope.id),
    );
    return !allSelected && someSelected;
  }

  changeScopeSelectionDos(e: { node: ScopeGroupFlatNode; isSelected: boolean }) {
    if (e.isSelected) {
      this.addScopeToSelection(this.mapFlatTreeNodeToScope(e.node));
    } else {
      this.removeScopeFromSelection(e.node.id);
    }
    this.form.markAsDirty();
  }

  changeGroupSelectionDos(e: { flatNode: ScopeGroupFlatNode; isSelected: boolean }) {
    const scopeGroup: ScopeGroup = this.flatNodeMap.get(e.flatNode);
    if (e.isSelected) {
      scopeGroup.scopes.forEach((scope) => {
        if (!this.isScopeSelected(scope.id)) {
          this.addScopeToSelection(scope);
        }
      });
    } else {
      scopeGroup.scopes.forEach((scope) => {
        if (this.isScopeSelected(scope.id)) {
          this.removeScopeFromSelection(scope.id);
        }
      });
    }
    this.form.markAsDirty();
  }

  revokeApiKey() {
    const title = 'Are you sure you want to revoke this key?';
    this.showRevokeApiKeyModal(title, '');
  }

  activateApiKey() {
    this.store.dispatch(activateApiKey({ apiKeyId: this.apiKey?.id }));
    this.dialogRef.close();
  }

  onSaveDos(e: { apiKey: ApiKey; form }) {
    if (e.apiKey) {
      const updatedApiKey = {
        ...e.apiKey,
        ...e.form.value,
      };
      this.store.dispatch(updateApiKey({ apiKey: updatedApiKey }));
    } else {
      this.store.dispatch(createApiKey({ apiKey: e.form.value }));
    }
    this.dialogRef.close();
  }

  private addScopeToSelection(scope: Scope) {
    this.selectedScopes = [...this.selectedScopes, scope];
  }

  private removeScopeFromSelection(scopeId: string) {
    const scopeIndex = this.selectedScopes.findIndex((scope) => scope.id === scopeId);
    if (scopeIndex !== -1) {
      const scopesCopy = [...this.selectedScopes];
      scopesCopy.splice(scopeIndex, 1);
      this.selectedScopes = scopesCopy;
    }
  }

  private mapFlatTreeNodeToScope(node: ScopeGroupFlatNode): Scope {
    return {
      id: node.id,
      name: node.name,
    };
  }

  private isScopeSelected(scopeId: string): boolean {
    return this.selectedScopes.some((scope) => scope.id === scopeId);
  }

  private showDiscardChangesModal(title: string, message: string) {
    this.store.dispatch(
      new ModalShow({
        title,
        message,
      }),
    );

    this.confirmSubscription = this.isConfirmed$.subscribe(() => {
      this.unsubscribe();
      this.dialogRef.close();
    });
  }

  private showRevokeApiKeyModal(title: string, message: string) {
    this.store.dispatch(
      new ModalShow({
        title,
        message,
      }),
    );

    this.confirmSubscription = this.isConfirmed$.subscribe(() => {
      this.store.dispatch(revokeApiKey({ apiKeyId: this.apiKey?.id }));
      this.unsubscribe();
      this.dialogRef.close();
    });
  }

  unsubscribe() {
    if (!!this.confirmSubscription) {
      this.confirmSubscription.unsubscribe();
    }
  }

  closeDialog() {
    this.store.dispatch(
      new ModalShow({
        title: 'Are you sure you want to leave this page?',
        message: 'All unsaved changes will be lost.',
      }),
    );
    this.store.select(isModalConfirmed).subscribe((close) => {
      if (close) {
        this.dialogRef.close();
      }
    });
  }
}
